import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type { ReactFlowInstance } from "reactflow";
import type * as Y from "yjs";
import Ajv from "ajv";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../api/client";
import {
  dataBool,
  dataStr,
  dataValue,
  type ReactFlowCanvasNode,
  type RawCanvasYNode,
  type ReactFlowCanvasNodeData,
  type ReactFlowCanvasEdgeData,
} from "../hooks/useYCanvas";
import {
  bindImageCanvasNode,
  type JsonValue,
} from "../generated/nodeDataTypes";
import { getPickedIndex } from "../components/canvas-v2/nodes/shared/designerUtils";
import { snapToClosestAspectWidth } from "../components/canvas-v2/nodes/shared/constants";
import {
  getNodeDimensions,
  getNodeSchema,
} from "../components/canvas-v2/nodeRegistry";
import type { ArrangeRequest } from "../generated/canvasArrange/ArrangeRequest";
import type { ArrangeResponse } from "../generated/canvasArrange/ArrangeResponse";
import responseSchema from "../generated/canvasArrange/ArrangeResponse.json";
import { createNadDebug } from "../utils/debugLog";
import { isShotListLayout, SHOT_LIST_NODE_DIMS } from "../components/canvas-v2/nodes/StoryboardDesignerNode/shotListLayout";

const log = createNadDebug("dw:layout:auto-layout");
const validateResponse = new Ajv().compile(responseSchema);
function isArrangeResponse(
  value: object | string | number | boolean | null,
): value is ArrangeResponse {
  return validateResponse(value) === true;
}
export function decodeResponse(value: JsonValue): ArrangeResponse {
  if (!isArrangeResponse(value))
    throw new Error(
      `Invalid arrangement response: ${JSON.stringify(validateResponse.errors)}`,
    );
  // The generated JSON Schema and interface have the same Pydantic owner.
  return value;
}

function layoutSnapshotFingerprint(snapshot: ArrangeRequest): string {
  // Selection is planning context, not a geometry mutation. Keep dimensions
  // here: newly loaded media can invalidate the proposed rectangle packing.
  return JSON.stringify({
    ...snapshot,
    nodes: snapshot.nodes.map((node) => ({ ...node, selected: false })),
  });
}

export function layoutSnapshot(
  rf: Pick<
    ReactFlowInstance<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>,
    "getNodes"
  >,
  doc: Y.Doc,
): ArrangeRequest {
  const visible = rf
    .getNodes()
    .filter((n) => n.type !== "director" && n.type !== "directorAgent");
  const ids = new Set(visible.map((n) => n.id));
  const provenance = visible.flatMap((n) => {
    const target = dataStr(n.data, "last_export_node_id");
    return target && ids.has(target)
      ? [
          {
            source: n.id,
            target,
            source_port: "verified_export",
            target_port: "source_editor",
            from_index: null,
            to_index: null,
          },
        ]
      : [];
  });
  const layoutNodes = visible
    .map((n: ReactFlowCanvasNode) => {
      const fallback = getNodeDimensions(
        n.type ?? "",
        dataStr(n.data, "in_aspect_ratio"),
      );
      const dimensions = isShotListLayout(n) ? { ...SHOT_LIST_NODE_DIMS, footerHeight: 0 } : fallback;
      let width = Math.max(n.width ?? 0, dimensions.width);
      let height = Math.max(
        n.height ?? 0,
        dimensions.height + dimensions.footerHeight,
      );
      if (n.type === "image") {
        const raw = doc.getMap<RawCanvasYNode>("nodes").get(n.id);
        const output = raw
          ? bindImageCanvasNode(raw).get("out_image")
          : undefined;
        const metadata =
          output?.history?.[getPickedIndex(output)]?.result?.metadata;
        const ratio = metadata?.aspect_ratio?.split(/[:/]/).map(Number);
        const loadedImage = document.querySelector<HTMLImageElement>(
          `.react-flow__node[data-id="${CSS.escape(n.id)}"] img`,
        );
        const naturalW =
          loadedImage?.naturalWidth || metadata?.actual_width || ratio?.[0];
        const naturalH =
          loadedImage?.naturalHeight || metadata?.actual_height || ratio?.[1];
        // Uploaded media changes from a default landscape shell after loading.
        // Prefer selected-asset metadata; unknown legacy assets reserve portrait space.
        const mediaWidth =
          naturalW && naturalH
            ? snapToClosestAspectWidth(naturalW, naturalH)
            : fallback.width;
        const mediaHeight =
          naturalW && naturalH
            ? (mediaWidth * naturalH) / naturalW
            : (fallback.width * 16) / 9;
        width = Math.max(width, mediaWidth);
        height = Math.max(height, mediaHeight);
      }
      if (
        getNodeSchema(n.type ?? "")?.inputs.some(
          (port) => port.id === "in_child_node_ids",
        )
      ) {
        // Offscreen groups have no DOM measurement. Their canonical RF style
        // and the descendant envelope below still reserve the full footprint.
        if (typeof n.style?.width === "number")
          width = Math.max(width, n.style.width);
        if (typeof n.style?.height === "number")
          height = Math.max(height, n.style.height);
      }
      const childIds = dataValue(n.data, "in_child_node_ids");
      return {
        children: Array.isArray(childIds)
          ? childIds.filter((id): id is string => typeof id === "string")
          : [],
        selected: n.selected === true,
        created_at: dataStr(n.data, "created_at") || null,
        user_edited_at: dataStr(n.data, "last_user_edit_at") || null,
        id: n.id,
        kind: n.type ?? "",
        label: dataStr(n.data, "label", dataStr(n.data, "name")).slice(0, 300),
        description: [
          "in_prompt",
          "in_motion_prompt",
          "in_description",
          "description",
          "in_instructions",
          "in_name",
          "name",
        ]
          .map((k) => dataStr(n.data, k))
          .filter(Boolean)
          .join("\n")
          .slice(0, 1000),
        parent: n.parentId ?? n.parentNode ?? null,
        width,
        height,
        x: n.position.x,
        y: n.position.y,
        fixed:
          dataBool(n.data, "pinned") === true ||
          (!n.parentId && !n.parentNode && n.draggable === false),
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
  // A group's visible border can be smaller than its full-chrome descendants.
  // Reserve their envelope while preserving every child coordinate and membership.
  for (let depth = 0; depth < layoutNodes.length; depth++) {
    let changed = false;
    for (const child of layoutNodes) {
      if (!child.parent) continue;
      const parent = layoutNodes.find((n) => n.id === child.parent);
      if (!parent) throw new Error("Missing canvas group");
      const width = Math.max(parent.width, child.x + child.width);
      const height = Math.max(parent.height, child.y + child.height);
      if (width !== parent.width || height !== parent.height) {
        parent.width = width;
        parent.height = height;
        changed = true;
      }
    }
    if (!changed) break;
    if (depth === layoutNodes.length - 1)
      throw new Error("Invalid canvas group ancestry");
  }
  return {
    nodes: layoutNodes,
    edges: [
      ...provenance,
      ...Array.from(doc.getMap<RawCanvasYNode>("edges").values()).flatMap(
        (edge) => {
          const source = edge.get("source"),
            target = edge.get("target");
          if (typeof source !== "string" || typeof target !== "string")
            throw new Error("Invalid canvas dependency");
          if (!ids.has(source) || !ids.has(target)) return [];
          const from = edge.get("from_port"),
            to = edge.get("to_port");
          const fromIndex = edge.get("fromIndex"),
            toIndex = edge.get("toIndex");
          return [
            {
              source,
              target,
              source_port: typeof from === "string" ? from : "",
              target_port: typeof to === "string" ? to : "",
              from_index: typeof fromIndex === "number" ? fromIndex : null,
              to_index: typeof toIndex === "number" ? toIndex : null,
            },
          ];
        },
      ),
    ].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
  };
}

// Observe authoritative layout facts, not generation progress/history. This
// closes the one-frame gap between a live-room update and ReactFlow projection.
export function layoutDocumentFingerprint(doc: Y.Doc): string {
  const fields = [
    "type",
    "label",
    "x",
    "y",
    "width",
    "height",
    "parentNode",
    "pinned",
    "in_child_node_ids",
    "in_layout_mode",
    "in_collapsed",
    "in_collapsible",
    "in_prompt",
    "in_motion_prompt",
    "in_description",
    "description",
    "in_instructions",
    "in_name",
    "name",
    "in_aspect_ratio",
    "last_export_node_id",
  ];
  const nodes = Array.from(doc.getMap<RawCanvasYNode>("nodes").entries())
    .map(([id, node]) => [id, fields.map((key) => [key, node.get(key)])])
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  const edges = Array.from(doc.getMap<RawCanvasYNode>("edges").entries())
    .map(([id, edge]) => [id, edge.toJSON()])
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  return JSON.stringify([nodes, edges]);
}

export function validateArrangement(
  snapshot: ArrangeRequest,
  result: ArrangeResponse,
): void {
  const roots = new Map(
    snapshot.nodes.filter((n) => !n.parent).map((n) => [n.id, n]),
  );
  if (result.positions.length !== roots.size)
    throw new Error("Arrangement omitted canvas nodes");
  const fixedRoots = new Set(
    snapshot.nodes
      .filter((n) => n.fixed)
      .map((node) => {
        let current = node;
        const seen = new Set<string>();
        while (current.parent) {
          if (seen.has(current.id)) throw new Error("Invalid group ancestry");
          seen.add(current.id);
          const parent = snapshot.nodes.find((n) => n.id === current.parent);
          if (!parent) throw new Error("Missing group parent");
          current = parent;
        }
        return current.id;
      }),
  );
  const seen = new Set<string>();
  for (const position of result.positions) {
    const node = roots.get(position.id);
    if (
      !node ||
      seen.has(position.id) ||
      !Number.isFinite(position.x) ||
      !Number.isFinite(position.y)
    )
      throw new Error("Invalid arrangement coordinates or node IDs");
    if (
      fixedRoots.has(node.id) &&
      (position.x !== node.x || position.y !== node.y)
    )
      throw new Error("Arrangement moved a fixed node");
    seen.add(position.id);
  }
}

export interface ArrangementUndo {
  isCurrent: () => boolean;
  restore: () => boolean;
}

interface ArrangementReview {
  undo: ArrangementUndo;
  fingerprint: string;
  projectId?: string;
  canvasId: string;
  ydoc: Y.Doc;
}

interface Options {
  projectId?: string;
  enabled: boolean;
  canvasId: string;
  ydoc: Y.Doc;
  instance: MutableRefObject<Pick<
    ReactFlowInstance<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>,
    "getNodes" | "fitView"
  > | null>;
  recordForUndo: () => ArrangementUndo | null;
  writeNodePosition: (id: string, x: number, y: number) => void;
}

export function useArrangeCanvas({
  projectId,
  enabled,
  canvasId,
  ydoc,
  instance,
  recordForUndo,
  writeNodePosition,
}: Options) {
  const { t } = useTranslation("workspace");
  const [arranging, setArranging] = useState(false);
  const [review, setReview] = useState<ArrangementReview | null>(null);
  const pending = useRef<AbortController | null>(null);
  const scope = useRef({ projectId, enabled, canvasId, ydoc });
  scope.current = { projectId, enabled, canvasId, ydoc };
  useEffect(() => {
    scope.current = { projectId, enabled, canvasId, ydoc };
    setArranging(false);
    setReview(null);
    return () => {
      pending.current?.abort();
      pending.current = null;
      scope.current = {
        projectId: undefined,
        enabled: false,
        canvasId: "",
        ydoc,
      };
    };
  }, [projectId, enabled, canvasId, ydoc]);
  const reviewIsCurrent = useCallback((value: ArrangementReview) => {
    const current = scope.current;
    return (
      current.enabled &&
      current.projectId === value.projectId &&
      current.canvasId === value.canvasId &&
      current.ydoc === value.ydoc &&
      value.undo.isCurrent() &&
      layoutDocumentFingerprint(value.ydoc) === value.fingerprint
    );
  }, []);
  useEffect(() => {
    if (!review) return;
    let valid = true;
    const check = () => {
      if (valid && !reviewIsCurrent(review)) {
        valid = false;
        setReview(null);
      }
    };
    check();
    review.ydoc.on("afterTransaction", check);
    return () => review.ydoc.off("afterTransaction", check);
  }, [review, reviewIsCurrent]);
  const keepArrangement = useCallback(() => {
    setReview(null);
    log({ event: "arrange:keep", project_id: projectId, canvas_id: canvasId });
  }, [projectId, canvasId]);
  const restoreArrangement = useCallback(() => {
    if (!review) return;
    setReview(null);
    if (
      !reviewIsCurrent(review) ||
      instance.current?.getNodes().some((node) => node.dragging) ||
      !review.undo.restore()
    ) {
      toast.error(t("canvasArea.arrangeStale"));
      return;
    }
    log({
      event: "arrange:restore",
      project_id: projectId,
      canvas_id: canvasId,
    });
  }, [review, reviewIsCurrent, instance, projectId, canvasId, t]);
  const arrange = useCallback(async () => {
    const rf = instance.current;
    if (!rf || !projectId || !enabled || pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    setArranging(true);
    setReview(null);
    const start = performance.now();
    try {
      if (rf.getNodes().some((n) => n.dragging))
        throw new Error(t("canvasArea.arrangeStale"));
      const snapshot = layoutSnapshot(rf, ydoc);
      log({
        event: "arrange:requested",
        project_id: projectId,
        canvas_id: canvasId,
        nodes: snapshot.nodes.length,
      });
      const before = layoutSnapshotFingerprint(snapshot);
      const documentBefore = layoutDocumentFingerprint(ydoc);
      const result = decodeResponse(
        await apiRequest<JsonValue>(
          `/projects/${encodeURIComponent(projectId)}/workspaces/canvases/${encodeURIComponent(canvasId)}/arrange`,
          {
            method: "POST",
            body: JSON.stringify(snapshot),
            retryTransient: false,
            signal: controller.signal,
          },
        ),
      );
      const current = scope.current;
      if (
        !current.enabled ||
        controller.signal.aborted ||
        current.projectId !== projectId ||
        current.canvasId !== canvasId ||
        current.ydoc !== ydoc ||
        rf.getNodes().some((n) => n.dragging) ||
        layoutDocumentFingerprint(ydoc) !== documentBefore ||
        layoutSnapshotFingerprint(layoutSnapshot(rf, ydoc)) !== before
      ) {
        throw new Error(t("canvasArea.arrangeStale"));
      }
      validateArrangement(snapshot, result);
      // Recheck authoritative local positions before the first mutation: RF can
      // lag one render behind a concurrent live-room update.
      const table = ydoc.getMap<RawCanvasYNode>("nodes");
      for (const node of snapshot.nodes.filter((n) => !n.parent)) {
        const live = table.get(node.id);
        if (
          !live ||
          live.get("parentNode") ||
          live.get("x") !== node.x ||
          live.get("y") !== node.y
        )
          throw new Error(t("canvasArea.arrangeStale"));
      }
      const prior = new Map(snapshot.nodes.map((n) => [n.id, n]));
      const moves = result.positions.filter(
        (p) => prior.get(p.id)?.x !== p.x || prior.get(p.id)?.y !== p.y,
      );
      if (moves.length) {
        const undo = recordForUndo();
        if (!undo || !undo.isCurrent())
          throw new Error(t("canvasArea.arrangeStale"));
        ydoc.transact(() => {
          for (const p of moves) writeNodePosition(p.id, p.x, p.y);
        }, "canvas-arrange");
        setReview({
          undo,
          fingerprint: layoutDocumentFingerprint(ydoc),
          projectId,
          canvasId,
          ydoc,
        });
      }
      log({
        event: "arrange:applied",
        project_id: projectId,
        canvas_id: canvasId,
        ...result.stats,
        units: result.positions.length,
        moves: moves.length,
        click_ms: performance.now() - start,
      });
      toast.success(t("canvasArea.arrangeComplete"));
      // Wait for the live projection to settle before fitting the explicit result.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (
            scope.current.projectId === projectId &&
            scope.current.ydoc === ydoc &&
            scope.current.canvasId === canvasId
          )
            rf.fitView({
              padding: 0.15,
              duration: 250,
              minZoom: 0.02,
              maxZoom: 1,
            });
        }),
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      const message = error instanceof Error ? error.message : String(error);
      log("failed reason=%s elapsedMs=%d", message, performance.now() - start);
      toast.error(message);
    } finally {
      if (pending.current === controller) {
        pending.current = null;
        setArranging(false);
      }
    }
  }, [
    projectId,
    enabled,
    canvasId,
    ydoc,
    instance,
    recordForUndo,
    writeNodePosition,
    t,
  ]);
  return {
    arrange,
    arranging,
    awaitingDecision: review !== null,
    keepArrangement,
    restoreArrangement,
  };
}
